import type { WidgetEvent, WidgetEventType } from '@springboard/shared';

export interface TrackerInit {
  apiBase: string;
  siteKey: string;
  vid: string;
  sid: string;
  variant: 'control' | 'variation';
  experimentId?: string;
  configVersion?: number;
  /** When false nothing leaves the browser (consent withheld or preview mode). */
  enabled: boolean;
}

/** Batches widget events and ships them to POST /v1/events (planning.md §8.4, Appendix C). */
export class Tracker {
  private queue: WidgetEvent[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly converted = new Set<string>();
  enabled: boolean;

  constructor(private readonly init: TrackerInit) {
    this.enabled = init.enabled;
    addEventListener('pagehide', () => this.flush(true));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.flush(true);
    });
  }

  get variant(): 'control' | 'variation' {
    return this.init.variant;
  }

  track(type: WidgetEventType, props: WidgetEvent['props'] = {}): void {
    if (!this.enabled) return;
    this.queue.push({
      v: 1,
      ts: new Date().toISOString(),
      siteKey: this.init.siteKey,
      vid: this.init.vid,
      sid: this.init.sid,
      variant: this.init.variant,
      experimentId: this.init.experimentId,
      configVersion: this.init.configVersion,
      type,
      path: location.pathname + location.search,
      ref: document.referrer ? document.referrer.slice(0, 2048) : undefined,
      props,
    });
    if (this.queue.length >= 10) this.flush();
    else if (!this.timer) this.timer = setTimeout(() => this.flush(), 2000);
  }

  /** Conversions are counted once per goal per session. */
  conversion(goalId: string): void {
    if (this.converted.has(goalId)) return;
    this.converted.add(goalId);
    this.track('conversion', { goalId });
    this.flush(true);
  }

  flush(beacon = false): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (!this.queue.length) return;
    const body = JSON.stringify({ events: this.queue.splice(0, 50) });
    const url = `${this.init.apiBase}/v1/events`;
    if (beacon && 'sendBeacon' in navigator) {
      const ok = navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      if (ok) return;
    }
    fetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body, keepalive: true, credentials: 'omit' }).catch(() => {});
  }
}
